import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Trash2, Search, Landmark, CheckSquare, Square } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatDetail } from "@/lib/api";
import { useSaveWithRetry } from "@/lib/saveRetry";
import { fmtDate, formatIDR } from "@/lib/constants";
import { computeExpectedNet, computeTotalReceived } from "@/lib/paymentReceipts";
import { listEstatements } from "@/lib/kasBankEstatement";

/**
 * Payment Receipt dialog — 2026-09-24 (user request R99): TANPA input manual.
 * Pembayaran selalu bersumber dari mutasi e-statement (Kas & Bank → Input &
 * Categorize). User memilih satu/beberapa baris mutasi masuk; tanggal, nominal,
 * rekening, referensi, dan catatan ter-isi otomatis dari baris mutasi. COA
 * mutasi sudah diatur di tab Breakdown.
 *
 * Props:
 * - open, onOpenChange: dialog control.
 * - project: project object (id, project_name, client, income_entries).
 * - entry:   OPTIONAL — pre-selected income entry. Jika null, tampilkan invoice picker.
 * - onChanged: callback setelah receipt tersimpan.
 */
export default function PaymentReceiptDialog({ open, onOpenChange, project, entry: entryProp = null, onChanged }) {
  const [selectedEntryId, setSelectedEntryId] = useState(entryProp?.id || "");
  const [busy, setBusy] = useState(false);
  const [entriesLocal, setEntriesLocal] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loadingCand, setLoadingCand] = useState(false);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({}); // { row_id: amountString }
  // 2026-09-24 (user request): filter kandidat per rekening bank & periode e-statement.
  const [bankFilter, setBankFilter] = useState("");
  const [periodFilter, setPeriodFilter] = useState("");
  const [bankAccounts, setBankAccounts] = useState([]);
  const [periods, setPeriods] = useState([]);
  const { runSave: runReceipt, RetryDialog: ReceiptRetryDialog } = useSaveWithRetry();

  useEffect(() => {
    if (open && project) {
      setEntriesLocal(project.income_entries || []);
      setSelectedEntryId(entryProp?.id || "");
      setSel({});
      setQ("");
    }
  }, [open, project, entryProp]);

  const invoiceOptions = useMemo(() => {
    return (entriesLocal || []).filter((e) => !e.is_retention_claim && Number(e.amount) > 0);
  }, [entriesLocal]);

  const currentEntry = entriesLocal.find((e) => e.id === selectedEntryId) || null;
  const expectedNet = currentEntry ? computeExpectedNet(currentEntry) : 0;
  const totalReceived = currentEntry ? computeTotalReceived(currentEntry) : 0;
  const remaining = Math.max(0, expectedNet - totalReceived);
  const sortedReceipts = useMemo(
    () => [...(currentEntry?.payment_receipts || [])].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || "")),
    [currentEntry]
  );

  // Auto-suggest kandidat mutasi masuk (e-statement) utk invoice terpilih.
  const loadCand = async (query, bank = bankFilter, period = periodFilter) => {
    if (!project || !currentEntry) return;
    setLoadingCand(true);
    try {
      const { data } = await api.get("/kas-bank/reconciliation/movement-candidates", {
        params: {
          project_id: project.id, entry_id: currentEntry.id, q: query || undefined,
          account_id: bank || undefined, period_month: period || undefined,
        },
      });
      setCandidates(Array.isArray(data?.candidates) ? data.candidates : []);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal memuat kandidat mutasi.");
      setCandidates([]);
    } finally { setLoadingCand(false); }
  };

  useEffect(() => {
    if (open && currentEntry) { setSel({}); loadCand(""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentEntry?.id]);

  // Muat daftar rekening & periode e-statement utk dropdown filter (sekali per open).
  useEffect(() => {
    if (!open) return;
    api.get("/company-bank-accounts").then((r) => setBankAccounts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    listEstatements({}).then((ests) => {
      const ps = [...new Set((ests || []).map((e) => e.period_month).filter(Boolean))].sort().reverse();
      setPeriods(ps);
    }).catch(() => {});
  }, [open]);

  const toggleRow = (c) => setSel((prev) => {
    const next = { ...prev };
    if (next[c.row_id] !== undefined) delete next[c.row_id];
    else next[c.row_id] = String(Math.round(c.remaining_amount > 0.01 ? c.remaining_amount : c.amount));
    return next;
  });

  const chosen = candidates.filter((c) => sel[c.row_id] !== undefined);
  const totalSel = chosen.reduce((a, c) => a + (parseFloat(String(sel[c.row_id]).replace(/[^\d]/g, "")) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!currentEntry) { toast.error("Pilih invoice dulu"); return; }
    if (chosen.length === 0) { toast.error("Pilih minimal satu mutasi bank."); return; }
    for (const c of chosen) {
      const amt = parseFloat(String(sel[c.row_id]).replace(/[^\d]/g, "")) || 0;
      if (amt <= 0) { toast.error(`Nominal alokasi untuk mutasi ${fmtDate(c.date)} harus lebih dari 0.`); return; }
      // 2026-02-13 (user request): tolak alokasi di atas sisa nominal mutasi.
      if (amt > (c.remaining_amount ?? Infinity) + 0.01) {
        toast.error(`Alokasi ${formatIDR(amt)} melebihi sisa nominal mutasi ${fmtDate(c.date)} (sisa ${formatIDR(c.remaining_amount)}).`);
        return;
      }
      if (!c.bank_name || !c.account_no) { toast.error(`Mutasi ${fmtDate(c.date)} tidak punya data rekening — periksa master rekening.`); return; }
    }
    let allowOverpay = false;
    if (expectedNet > 0 && totalSel > remaining + 0.01) {
      const proceed = window.confirm(
        `Total alokasi (${formatIDR(totalSel)}) melebihi sisa ekspektasi Net (${formatIDR(remaining)}).\nLanjutkan? (akan dikirim allow_overpay=true)`
      );
      if (!proceed) return;
      allowOverpay = true;
    }
    runReceipt(async () => {
      let lastData = null;
      for (const c of chosen) {
        const amt = parseFloat(String(sel[c.row_id]).replace(/[^\d]/g, "")) || 0;
        const { data } = await api.post(
          `/active-projects/${project.id}/income/${currentEntry.id}/payments`,
          {
            date: c.date,
            amount: amt,
            bank_name: c.bank_name,
            account_no: c.account_no,
            bank_account_id: c.account_id || undefined,
            finance_source: "laporan_keuangan",
            idempotency_key: `pr-${currentEntry.id}-${c.row_id}-${Math.round(amt)}`,
            note: c.description ? `Dari mutasi bank: ${String(c.description).slice(0, 100)}` : undefined,
            transfer_ref: c.doc_no || undefined,
            estatement_row_id: c.row_id,
            allow_overpay: allowOverpay,
          }
        );
        lastData = data;
      }
      if (lastData) setEntriesLocal((es) => es.map((x) => (x.id === lastData.id ? lastData : x)));
      setSel({});
      toast.success(`${chosen.length} pembayaran tersimpan dari mutasi bank`);
      onChanged?.();
      loadCand(q);
    });
  };

  const del = async (receiptId) => {
    if (!window.confirm("Hapus catatan penerimaan ini?")) return;
    setBusy(true);
    try {
      const { data } = await api.delete(
        `/active-projects/${project.id}/income/${currentEntry.id}/payments/${receiptId}`
      );
      setEntriesLocal((es) => es.map((x) => (x.id === data.id ? data : x)));
      toast.success("Catatan penerimaan dihapus");
      onChanged?.();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal menghapus");
    } finally { setBusy(false); }
  };

  const pctReceived = expectedNet > 0 ? Math.min(100, (totalReceived / expectedNet) * 100) : 0;
  // 2026-02-13 (user report): toleransi Rp1 (parity dgn backend `_invoice_status`
  // yang memakai outstanding <= 1.0). Tanpa ini, selisih fraksi rupiah dari
  // perhitungan persen PPh membuat status "Diterima Sebagian" padahal sisa tampil Rp0.
  const isPartial = totalReceived > 0 && totalReceived + 1 < expectedNet;
  const statusLabel = totalReceived <= 0 ? "Belum Diterima"
    : isPartial ? "Diterima Sebagian"
    : "Lunas";
  const statusTone = totalReceived <= 0 ? "bg-amber-100 text-amber-800 border-amber-300"
    : isPartial ? "bg-sky-100 text-sky-800 border-sky-300"
    : "bg-emerald-100 text-emerald-800 border-emerald-300";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto rounded-none" data-testid="payment-receipt-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Penerimaan Pembayaran Client</DialogTitle>
          <DialogDescription className="text-xs">
            {project?.project_name} · {project?.client || "-"} · Sinkron dari mutasi e-statement
          </DialogDescription>
        </DialogHeader>

        {/* Invoice picker — hidden jika entry di-pre-select */}
        {!entryProp && (
          <div className="space-y-1.5" data-testid="pr-invoice-picker">
            <div className="overline text-slate-600">Pilih Invoice / Tagihan</div>
            <Select value={selectedEntryId} onValueChange={setSelectedEntryId}>
              <SelectTrigger className="rounded-none" data-testid="pr-invoice-select">
                <SelectValue placeholder="Pilih invoice yang akan diterima pembayarannya…" />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                {invoiceOptions.length === 0 && (
                  <SelectItem disabled value="__none">Belum ada invoice di project ini</SelectItem>
                )}
                {invoiceOptions.map((e) => {
                  const exp = computeExpectedNet(e);
                  const rec = computeTotalReceived(e);
                  const rem = Math.max(0, exp - rec);
                  const paid = rec >= exp - 0.01 && exp > 0;
                  return (
                    <SelectItem key={e.id} value={e.id} disabled={paid} data-testid={`pr-invoice-opt-${e.id}`}>
                      Inv {e.invoice_date ? fmtDate(e.invoice_date) : "(tanpa tgl)"} · {formatIDR(e.amount)} · Sisa {formatIDR(rem)}{paid ? " · LUNAS" : ""}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Ringkasan invoice */}
        {currentEntry && (
          <div className="border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="pr-invoice-summary">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Invoice</div>
                <div className="text-sm font-semibold text-[#0B1D3A]">
                  {currentEntry.invoice_date ? fmtDate(currentEntry.invoice_date) : "-"} · {formatIDR(currentEntry.amount)}
                </div>
                {currentEntry.note && <div className="text-[10px] italic text-slate-500 mt-0.5">"{currentEntry.note}"</div>}
              </div>
              <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider border ${statusTone}`} data-testid="pr-status">
                {statusLabel}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-[10px] pt-1 border-t border-slate-200">
              <div className="bg-white border border-slate-200 px-2 py-1.5">
                <div className="overline text-slate-500">Kas Harus Diterima</div>
                <div className="tabular-nums font-semibold text-[#0B1D3A]" data-testid="pr-expected">{formatIDR(expectedNet)}</div>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 px-2 py-1.5">
                <div className="overline text-emerald-700">Sudah Diterima</div>
                <div className="tabular-nums font-semibold text-emerald-800" data-testid="pr-received">{formatIDR(totalReceived)}</div>
              </div>
              <div className="bg-rose-50 border border-rose-200 px-2 py-1.5">
                <div className="overline text-rose-700">Sisa</div>
                <div className="tabular-nums font-semibold text-rose-800" data-testid="pr-remaining">{formatIDR(remaining)}</div>
              </div>
            </div>
            {expectedNet > 0 && (
              <div>
                <div className="flex items-center justify-between text-[9px] uppercase tracking-wider text-slate-500 mb-1">
                  <span>Progress Pembayaran</span>
                  <span className="tabular-nums font-semibold text-emerald-700" data-testid="pr-progress-label">{pctReceived.toFixed(1)}%</span>
                </div>
                <div className="h-1.5 bg-slate-200">
                  <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pctReceived}%` }} data-testid="pr-progress-bar" />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pilih mutasi bank (e-statement) — TANPA input manual */}
        {currentEntry && (
          <form onSubmit={submit} className="border border-slate-200 p-3 space-y-3" data-testid="pr-form">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 flex items-center gap-1.5">
                <Landmark size={12} className="text-[#0B1D3A]" /> Pilih Mutasi Uang Masuk (E-Statement)
              </div>
              <div className="text-[9px] text-slate-400 italic">Auto-suggest: penerimaan ±3 s/d 60 hari dari tanggal invoice</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select value={bankFilter} onChange={(e) => { setBankFilter(e.target.value); loadCand(q, e.target.value, periodFilter); }}
                data-testid="pr-move-bank-filter"
                className="border border-slate-300 px-2 py-1.5 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]">
                <option value="">Semua Bank / Rekening</option>
                {bankAccounts.map((a) => <option key={a.id} value={a.id}>{a.bank_name} · {a.account_no}</option>)}
              </select>
              <select value={periodFilter} onChange={(e) => { setPeriodFilter(e.target.value); loadCand(q, bankFilter, e.target.value); }}
                data-testid="pr-move-period-filter"
                className="border border-slate-300 px-2 py-1.5 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]">
                <option value="">Semua Periode E-Statement</option>
                {periods.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={q}
                onChange={(e) => { setQ(e.target.value); loadCand(e.target.value); }}
                data-testid="pr-move-search"
                placeholder="Cari deskripsi / nominal (menjangkau seluruh riwayat, di luar jendela 60 hari)…"
                className="w-full pl-7 pr-2 py-1.5 border border-slate-300 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]"
              />
            </div>
            <div className="border border-slate-200 max-h-64 overflow-y-auto">
              {loadingCand ? (
                <div className="p-5 text-center text-[11px] italic text-slate-400">Memuat kandidat…</div>
              ) : candidates.length === 0 ? (
                <div className="p-5 text-center text-[11px] text-slate-400" data-testid="pr-move-empty">
                  Tidak ada mutasi masuk yang cocok. Coba kolom pencarian, atau import e-statement dulu di Kas &amp; Bank.
                </div>
              ) : (
                <table className="w-full text-[10px]">
                  <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 text-[9px] uppercase tracking-wider text-slate-600">
                    <tr>
                      <th className="px-2 py-2 w-8"></th>
                      <th className="text-left px-3 py-2">Tanggal</th>
                      <th className="text-left px-3 py-2">Deskripsi</th>
                      <th className="text-left px-3 py-2">Rekening</th>
                      <th className="text-right px-3 py-2">Nominal</th>
                      <th className="text-right px-3 py-2">Sisa Belum Alokasi</th>
                      <th className="text-right px-3 py-2">Alokasi ke Invoice Ini</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map((c) => {
                      const isSel = sel[c.row_id] !== undefined;
                      const fullyAllocated = c.remaining_amount <= 0.01 && !isSel;
                      return (
                        <tr key={c.row_id} className={`border-b border-slate-100 ${isSel ? "bg-amber-50/60" : "hover:bg-slate-50"}`} data-testid={`pr-move-row-${c.row_id}`}>
                          <td className="px-2 py-1.5">
                            <button type="button" onClick={() => toggleRow(c)} disabled={fullyAllocated}
                              data-testid={`pr-move-check-${c.row_id}`}
                              title={fullyAllocated ? "Mutasi sudah 100% ter-alokasi" : "Pilih mutasi ini"}
                              className="inline-flex items-center justify-center text-slate-500 hover:text-[#FF5A1F] disabled:opacity-30">
                              {isSel ? <CheckSquare size={14} className="text-[#FF5A1F]" /> : <Square size={14} />}
                            </button>
                          </td>
                          <td className="px-3 py-1.5 tabular-nums whitespace-nowrap">{fmtDate(c.date)}</td>
                          <td className="px-3 py-1.5">
                            <div className="text-slate-800">{c.description || "—"}</div>
                            {c.doc_no && <div className="text-[9px] text-slate-500">Ref: {c.doc_no}</div>}
                          </td>
                          <td className="px-3 py-1.5"><div>{c.bank_name || "—"}</div><div className="text-[9px] text-slate-500">{c.account_no || ""}</div></td>
                          <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-emerald-700">{formatIDR(c.amount)}</td>
                          <td className={`px-3 py-1.5 text-right tabular-nums ${c.remaining_amount <= 0.01 ? "text-slate-400" : "text-amber-700 font-semibold"}`}>{formatIDR(c.remaining_amount)}</td>
                          <td className="px-3 py-1.5 text-right">
                            {isSel ? (
                              <input
                                inputMode="numeric"
                                value={Number(sel[c.row_id] || 0).toLocaleString("id-ID")}
                                onChange={(e) => {
                                  // 2026-02-13 (user request): nominal alokasi TIDAK boleh
                                  // melebihi sisa nominal mutasi — clamp otomatis ke sisa.
                                  const n = parseFloat(e.target.value.replace(/[^\d]/g, "")) || 0;
                                  const max = Math.max(0, Math.round(c.remaining_amount));
                                  setSel((prev) => ({ ...prev, [c.row_id]: String(Math.min(n, max)) }));
                                }}
                                title={`Maksimal ${formatIDR(c.remaining_amount)} (sisa nominal mutasi)`}
                                data-testid={`pr-move-amount-${c.row_id}`}
                                className="w-28 border border-[#FF5A1F] px-1.5 py-1 text-right tabular-nums text-[11px] font-bold text-[#FF5A1F] bg-white focus:outline-none"
                              />
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[11px] text-slate-600" data-testid="pr-sel-summary">
                {chosen.length === 0
                  ? "Belum ada mutasi dipilih."
                  : <span><b className="text-[#0B1D3A]">{chosen.length} mutasi</b> dipilih · total alokasi <b className="tabular-nums text-emerald-700">{formatIDR(totalSel)}</b></span>}
              </div>
              <button type="submit" disabled={busy || chosen.length === 0} data-testid="pr-sync-submit"
                className="inline-flex items-center gap-1 px-4 py-2 bg-[#0B1D3A] hover:bg-[#122a4d] disabled:opacity-40 text-white text-[11px] uppercase tracking-wider font-semibold">
                Simpan Penerimaan
              </button>
            </div>
          </form>
        )}

        {/* Riwayat receipts */}
        {currentEntry && sortedReceipts.length > 0 && (
          <div className="border border-slate-200" data-testid="pr-history">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-semibold text-slate-600">
              Riwayat Penerimaan ({sortedReceipts.length})
            </div>
            <ol className="divide-y divide-slate-100">
              {sortedReceipts.map((r, idx) => (
                <li key={r.id} className="px-3 py-2 flex items-start justify-between gap-3 hover:bg-slate-50" data-testid={`pr-item-${r.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#0B1D3A] text-xs tabular-nums">
                        #{sortedReceipts.length - idx} · {fmtDate(r.date)}
                      </span>
                      <span className="font-bold text-emerald-700 text-xs tabular-nums">
                        {formatIDR(r.amount)}
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {r.bank_name} · {r.account_no}
                    </div>
                    {r.transfer_ref && <div className="text-[10px] text-slate-600 font-mono mt-0.5">Ref: {r.transfer_ref}</div>}
                    {r.estatement_row_id && (
                      <div className="mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-800 text-[9px] uppercase tracking-wider font-semibold" data-testid={`pr-item-linked-${r.id}`}>
                        <Landmark size={9} /> Dari mutasi bank
                      </div>
                    )}
                    {r.note && <div className="text-[10px] italic text-slate-600 mt-0.5">"{r.note}"</div>}
                    <div className="text-[9px] text-slate-400 mt-0.5">Oleh {r.created_by_name || "-"}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => del(r.id)}
                    disabled={busy}
                    data-testid={`pr-delete-${r.id}`}
                    className="text-rose-600 hover:text-rose-800 disabled:opacity-50 mt-0.5"
                    title="Hapus (koreksi)"
                  >
                    <Trash2 size={13} />
                  </button>
                </li>
              ))}
            </ol>
          </div>
        )}

        {currentEntry && sortedReceipts.length === 0 && (
          <div className="text-xs italic text-slate-400 text-center py-3 border border-dashed border-slate-200" data-testid="pr-empty">
            Belum ada catatan pembayaran untuk invoice ini.
          </div>
        )}

        <ReceiptRetryDialog />
      </DialogContent>
    </Dialog>
  );
}
